import type {
  IBuildAgentSelectionsProps,
  IChangeAgentSelectionModelProps,
  IFillMissingAgentModelsProps,
  ISelectedAgent,
  IToggleAgentSelectionProps,
  TAgentSelectionMap,
} from "../types/agent-import";

// NOTE: NSAI-6593 — the selection rules every agent import shares on web and
// mobile: every agent starts ticked with the default model, a model the user
// picked is never overwritten, and Import needs a model on every ticked agent.

export const buildAgentSelections = ({
  agentIds,
  defaultModelUuid,
}: IBuildAgentSelectionsProps): TAgentSelectionMap =>
  Object.fromEntries(
    agentIds.map((agentId) => [
      agentId,
      { isSelected: true, modelUuid: defaultModelUuid },
    ]),
  );

// NOTE: Returns the same object when nothing changes, so a React state setter
// can skip the re-render.
export const fillMissingAgentModels = ({
  selections,
  defaultModelUuid,
}: IFillMissingAgentModelsProps): TAgentSelectionMap => {
  if (!defaultModelUuid) return selections;

  const hasUnsetModel = Object.values(selections).some(
    (selection) => selection.modelUuid === "",
  );
  if (!hasUnsetModel) return selections;

  return Object.fromEntries(
    Object.entries(selections).map(([agentId, selection]) => [
      agentId,
      selection.modelUuid === ""
        ? { ...selection, modelUuid: defaultModelUuid }
        : selection,
    ]),
  );
};

export const toggleAgentSelection = ({
  selections,
  agentId,
}: IToggleAgentSelectionProps): TAgentSelectionMap => ({
  ...selections,
  [agentId]: {
    modelUuid: selections[agentId]?.modelUuid ?? "",
    isSelected: !selections[agentId]?.isSelected,
  },
});

export const changeAgentSelectionModel = ({
  selections,
  agentId,
  modelUuid,
}: IChangeAgentSelectionModelProps): TAgentSelectionMap => ({
  ...selections,
  [agentId]: {
    isSelected: selections[agentId]?.isSelected ?? true,
    modelUuid,
  },
});

export const getSelectedAgents = (
  selections: TAgentSelectionMap,
): ISelectedAgent[] =>
  Object.entries(selections)
    .filter(([, selection]) => selection.isSelected)
    .map(([agentId, selection]) => ({
      agentId,
      modelUuid: selection.modelUuid,
    }));

// NOTE: The server rejects the whole batch on an empty modelUuid, so an unset
// model on any selected agent must block Import.
export const canImportSelectedAgents = (
  selectedAgents: ISelectedAgent[],
): boolean =>
  selectedAgents.length > 0 &&
  selectedAgents.every((agent) => agent.modelUuid !== "");
